import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Handshake } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ScheduleChips } from "@/components/BudgetSchedule";
import useAutoRefresh from "@/lib/useAutoRefresh";
import RequestReopenButton, { canRequestReopen, FINAL_STATUSES } from "@/components/RequestReopenButton";

// Ordem de prioridade para a escola: primeiro o que ela precisa agir
// (aguardando → aprovado → iniciando → finalizado), depois o histórico fiscal
// (NFG → NFP) e por fim os encerrados (vencido / reprovado).
const STATUS_ORDER = [
  "aguardando",
  "aprovado",
  "iniciando",
  "finalizado",
  "nota_fiscal_gerada",
  "nota_faturada",
  "validade_expirada",
  "reprovado",
  "deletado",
  "ex_sistema",
];
const GROUP_LABEL = {
  aguardando: "Aguardando aprovação",
  aprovado: "Aprovados",
  iniciando: "Em andamento",
  finalizado: "Finalizados",
  nota_fiscal_gerada: "Nota fiscal gerada",
  nota_faturada: "Nota fiscal paga",
  validade_expirada: "Validade expirada",
  reprovado: "Reprovados",
  deletado: "Deletados",
  ex_sistema: "Ex sistema",
};

export default function SchoolBudgets() {
  const [budgets, setBudgets] = useState([]);

  const load = () => api.get("/school/budgets").then((r) => setBudgets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 15000);

  // Agrupa por status mantendo a ordem acima.  Dentro de cada grupo, mais
  // recentes primeiro (budget_date desc).
  const grouped = useMemo(() => {
    const map = {};
    for (const b of budgets) {
      const k = b.status || "aguardando";
      (map[k] = map[k] || []).push(b);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => String(b.budget_date || "").localeCompare(String(a.budget_date || "")));
    }
    return STATUS_ORDER.filter((s) => map[s]?.length).map((s) => ({ status: s, items: map[s] }));
  }, [budgets]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">Meus orçamentos</h1>
        <p className="text-sm text-slate-500">Agrupados por status — primeiro aguardando/aprovado, depois os demais</p>
      </div>

      {budgets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-12 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nenhum orçamento ainda</p>
          <p className="text-sm text-slate-500 mt-1">Peça um novo serviço para receber seu primeiro orçamento.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ status, items }) => (
            <section key={status} data-testid={`s-budgets-group-${status}`}>
              <div className="flex items-baseline justify-between mb-2 px-1">
                <h2 className="text-xs font-black uppercase tracking-wider text-[#1e3a8a]">
                  {GROUP_LABEL[status] || status}
                </h2>
                <span className="text-[11px] font-semibold text-slate-500">
                  {items.length} {items.length === 1 ? "item" : "itens"}
                </span>
              </div>
              <div className="space-y-3">
                {items.map((b) => {
                  const isClosed = FINAL_STATUSES.has(b.status);
                  const showReopen = canRequestReopen(b.status);
                  return (
                    <div
                      key={b.id}
                      data-testid={`s-budget-card-${b.number}`}
                      className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 hover:border-[#7c3aed]/60 transition-colors"
                    >
                      <Link
                        to={`/escola/orcamentos/${b.id}`}
                        data-testid={`s-budget-${b.number}`}
                        className="block"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center shrink-0">
                              <FileText className="w-5 h-5 text-[#7c3aed]" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono font-bold text-slate-900">#{b.number}</span>
                                <StatusBadge status={b.status} />
                                <ScheduleChips b={b} showPayment={false} />
                                {b.faz_depois_paga && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                    <Handshake className="w-3 h-3" /> FDP
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {formatDateBR(b.budget_date)} • {b.items?.length || 0} iten(s)
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-slate-900">{BRL(b.total)}</p>
                            {!isClosed && b.validity_date && (
                              <p className="text-[10px] text-slate-500">Validade: {formatDateBR(b.validity_date)}</p>
                            )}
                          </div>
                        </div>
                      </Link>
                      {showReopen && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <RequestReopenButton budget={b} onDone={load} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
